import { forwardRef, useCallback, useState } from "react";
import { Listing } from "src/types/listing";
import { ListingStats, ListingStatsProps } from "src/components/common/ListingStats";
import { ListingPropertyStatus } from "./ListingPropertyStatus";
import Button from "src/components/core/Button";
import { HeartIcon } from "../neighborhoodSearch/components/HeartIcon";
import PropertySkeleton from "../neighborhoodSearch/components/PropertyCardSkeleton";
import { Ellipsis } from "lucide-react";
import { StatusInfo } from "../common/PropertySummary";

enum EnumTitleAttribute {
  Owner = "Owner",
  Address = "Address",
}

export type PropertyCardProps = {
  onClick?: VoidFunction;
  onImageClick?: VoidFunction;
  listing: Listing;
  titleAttribute?: EnumTitleAttribute;
  suppressGutters?: boolean;
  isSuppressListingStats?: boolean;
  isSuppressPreview?: boolean;
  onFavoriteClick?: VoidFunction;
  isShowStarredIconWithTitle?: boolean;
  isLoading?: boolean;
  ListingStatsProps?: Partial<ListingStatsProps>;
  isFavoritingEnabled?: boolean;
};

const PropertyCard = forwardRef<
  HTMLDivElement,
  PropertyCardProps
>(
  (
    {
      onClick,
      onImageClick,
      listing,
      titleAttribute = EnumTitleAttribute.Address,
      suppressGutters = false,
      isSuppressListingStats = false,
      isSuppressPreview = false,
      onFavoriteClick,
      isShowStarredIconWithTitle = false,
      isLoading = false,
      ListingStatsProps,
      isFavoritingEnabled = false
    },
    ref
  ) => {
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);

    const handleImageClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onImageClick?.();
      },
      [onImageClick]
    );

    const handleImageKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onImageClick?.();
        }
      },
      [onImageClick]
    );

    const handleFavoriteClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onFavoriteClick?.();
      },
      [onFavoriteClick]
    );

    const handleImageLoad = () => {
      setImageLoading(false);
    };

    const handleImageError = () => {
      setImageLoading(false);
      setImageError(true);
    };

    if (isLoading || !listing) {
      return <PropertySkeleton />;
    }

    const { isFavorite = false } = listing;

    return (
      <div
        ref={ref}
        {...(onClick ? { onClick } : {})}
        className={`property-card group flex flex-col ${
          onClick ? "cursor-pointer" : ""
        } relative`}
      >
        {!isSuppressPreview && (
          <>
            <div
              onClick={onImageClick ? handleImageClick : undefined}
              {...(onImageClick && {
                role: "button",
                tabIndex: 0,
                onKeyDown: handleImageKeyDown,
                "aria-label": "Show on map",
              })}
              className={`relative aspect-[3/2] bg-gray-100 rounded-lg overflow-hidden ${
                onImageClick ? "cursor-pointer" : ""
              }`}
            >
              <div className="absolute top-0 left-0 z-10">
                <ListingPropertyStatus listing={listing} />
              </div>
              {imageLoading && (
                <div className="image-loading absolute inset-0 flex items-center justify-center z-20">
                  <div className="animate-pulse">
                    <Ellipsis className="w-12 h-12 text-gray-300" />
                  </div>
                </div>
              )}
              {imageError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-20">
                  <div className="text-center">
                    <svg className="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-gray-500 mt-2">Image unavailable</p>
                  </div>
                </div>
              )}
              <img
                className={`property-card-image absolute inset-0 w-full h-full object-cover ${imageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
                src={`https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${listing.geolocation?.lat},${listing.geolocation?.lng}&radius=100&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                alt="Street View Thumbnail"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </div>

            {onFavoriteClick && isFavoritingEnabled && (
              <Button
                onClick={handleFavoriteClick}
                variant="ghost"
                className={"absolute p-0 w-[30px] h-[30px] min-w-[30px] top-[10px] right-[10px] text-red-500 z-20"}
                type="button"
              >
                <HeartIcon isFavorite={isFavorite} />
              </Button>
            )}
          </>
        )}
        <div
          className={`flex flex-col gap-2 py-4 ${
            suppressGutters ? "" : "px-4"
          }`}
        >
          {isSuppressPreview && <StatusInfo listing={listing} />}
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1 ">
              <h2
                className={`${
                  onClick ? "group-hover:text-primary" : ""
                } font-semibold text-base`}
              >
                {titleAttribute === EnumTitleAttribute.Owner
                  ? listing?.ownerContactInfo?.fullName
                  : listing?.address?.formattedAddress}
              </h2>
              {isShowStarredIconWithTitle && isFavorite && (
                <HeartIcon isFavorite />
              )}
            </div>
          </div>
          <p className="text-sm font-normal text-muted-foreground">
            {titleAttribute === EnumTitleAttribute.Owner
              ? listing?.address?.formattedAddress
              : listing?.ownerContactInfo?.fullName}
          </p>
          {!isSuppressListingStats && (
            <ListingStats
              listing={listing}
              {...ListingStatsProps}
            />
          )}
        </div>
      </div>
    );
  }
);

export default PropertyCard;
